import { useRef, useEffect } from 'react'

/**
 * AtomBackground – Bohr-model atoms drawn on Canvas.
 * Inspired by classic 3-orbit atomic diagram: nucleus cluster +
 * three elliptical orbits rotated ~60° apart, each with one electron.
 * Minimal, dark-mode, gold-tinted. Used as section background.
 */
export default function AtomBackground({ className = '' }) {
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    let raf
    let t = 0

    // Palette
    const GOLD = (a) => `rgba(201,168,76,${a})`
    const WHITE = (a) => `rgba(240,237,230,${a})`

    /**
     * speed muchísimo más bajo
     * para un movimiento elegante y cinematográfico
     */
    const atoms = [
      { x: 0.14, y: 0.22, r: 58, speed: 0.0009, tilt: 0.15 },
      { x: 0.86, y: 0.58, r: 72, speed: 0.0007, tilt: 0.6 },
      { x: 0.50, y: 0.88, r: 46, speed: 0.0011, tilt: -0.2 },
    ]

    function resize() {
      canvas.width = canvas.offsetWidth
      canvas.height = canvas.offsetHeight
    }

    /**
     * Draw one Bohr-model atom
     */
    function drawAtom(atom, time) {
      const W = canvas.width
      const H = canvas.height

      const ax = atom.x * W
      const ay = atom.y * H

      const r = atom.r
      const ry = r * 0.36

      // ── Nucleus ───────────────────────────────────────────
      const nr = r * 0.11

      const clusterOffsets = [
        { dx: -nr * 0.55, dy: -nr * 0.55 },
        { dx: nr * 0.55, dy: -nr * 0.55 },
        { dx: -nr * 0.55, dy: nr * 0.55 },
        { dx: nr * 0.55, dy: nr * 0.55 },
      ]

      clusterOffsets.forEach(({ dx, dy }, i) => {
        ctx.beginPath()
        ctx.arc(ax + dx, ay + dy, nr, 0, Math.PI * 2)

        ctx.fillStyle = i % 2 === 0
          ? GOLD(0.28)
          : WHITE(0.14)

        ctx.fill()

        ctx.strokeStyle = i % 2 === 0
          ? GOLD(0.45)
          : WHITE(0.22)

        ctx.lineWidth = 0.6
        ctx.stroke()
      })

      // ── Orbits ────────────────────────────────────────────
      const ORBIT_COUNT = 3

      for (let o = 0; o < ORBIT_COUNT; o++) {
        const orbitAngle =
          atom.tilt + (Math.PI / ORBIT_COUNT) * o

        ctx.save()

        ctx.translate(ax, ay)
        ctx.rotate(orbitAngle)

        // Orbit line
        ctx.beginPath()
        ctx.ellipse(0, 0, r, ry, 0, 0, Math.PI * 2)

        ctx.strokeStyle = GOLD(0.13)
        ctx.lineWidth = 0.8
        ctx.stroke()

        // Electron
        const phase =
          (Math.PI * 2 / ORBIT_COUNT) * o

        /**
         * Movimiento MUCHO más lento
         */
        const eAngle =
          time * atom.speed * 60 + phase

        const ex = Math.cos(eAngle) * r
        const ey = Math.sin(eAngle) * ry

        // Glow
        const glow = ctx.createRadialGradient(
          ex,
          ey,
          0,
          ex,
          ey,
          5
        )

        glow.addColorStop(0, GOLD(0.38))
        glow.addColorStop(0.5, GOLD(0.1))
        glow.addColorStop(1, GOLD(0))

        ctx.beginPath()
        ctx.arc(ex, ey, 5, 0, Math.PI * 2)

        ctx.fillStyle = glow
        ctx.fill()

        // Electron core
        ctx.beginPath()
        ctx.arc(ex, ey, 2, 0, Math.PI * 2)

        ctx.fillStyle = GOLD(0.55)
        ctx.fill()

        ctx.strokeStyle = GOLD(0.75)
        ctx.lineWidth = 0.6
        ctx.stroke()

        ctx.restore()
      }
    }

    function draw() {
      const W = canvas.width
      const H = canvas.height

      ctx.clearRect(0, 0, W, H)

      atoms.forEach((a) => drawAtom(a, t))

      // Vignette
      const fade = ctx.createRadialGradient(
        W / 2,
        H / 2,
        H * 0.08,
        W / 2,
        H / 2,
        H * 0.72
      )

      fade.addColorStop(0, 'rgba(13,13,13,0)')
      fade.addColorStop(1, 'rgba(13,13,13,0.65)')

      ctx.fillStyle = fade
      ctx.fillRect(0, 0, W, H)
    }

    function loop() {
      t += 1

      draw()

      raf = requestAnimationFrame(loop)
    }

    resize()

    window.addEventListener('resize', resize)

    loop()

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', resize)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
      style={{
        opacity: 0.6,
      }}
    />
  )
}